import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ICreditOffersFilters } from '../models/offers-filters.model';
import { PageableModel } from '../models/pageable.model';
import { ICreditLines } from '../models/financing-request.model';
import { DateTime } from 'luxon';
import { comparison, eq, ge, le, and } from 'rsql-builder';
import { ICreditOffers } from '../models/financing-offers.model';

@Injectable({
  providedIn: 'root'
})
export class OffersApiService {

  constructor(private http: HttpClient) { }

  public getOfertaPlazosOptions(): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/financiamientos/oferta/portal/plazos-oferta`)
  }
  
  public createNewOffer(body: any): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/financiamientos/oferta/portal/save-offer`, body);
  }

  public getOfferById(offerId: number | string): Observable<ICreditOffers> {
    return this.http.get<ICreditOffers>(`${environment.apiBaseUrl}/financiamientos/oferta/portal/get-offer/${offerId}`);
  }

  public getRecievedOffers(filter: ICreditOffersFilters): Observable<PageableModel<ICreditLines>> {
    const comps = []
    let noFilter = true;
    if (filter?.razonSocial) {
      const razonSocialValue = filter.razonSocial.replace(/\s/g, '%');
      comps.push(`razonSocial==*${razonSocialValue}*`);
      noFilter = false;
    }
    if (filter?.score) {
      comps.push(comparison('score', eq(filter.score)));
      noFilter = false;
    }
    if (filter?.montoMin) {
      comps.push(comparison('montoRequerido', ge(filter.montoMin)));
      noFilter = false;
    }
    if (filter?.montoMax) {
      comps.push(comparison('montoRequerido', le(filter.montoMax)));
      noFilter = false;
    }
    if (filter?.motivo) {
      const motivoValue = filter.motivo.replace(/\s/g, '%');
      comps.push(`motivoDeFinanciamiento==*${motivoValue}*`);
      noFilter = false;
    }
    if (filter?.estado) {
      comps.push(comparison('status', eq(filter.estado)));
      noFilter = false;
    }
    if (filter?.intencionMin) {
      comps.push(comparison('intencionCredito', ge(filter.intencionMin)));
      noFilter = false;
    }
    if (filter?.intencionMax) {
      comps.push(comparison('intencionCredito', le(filter.intencionMax)));
      noFilter = false;
    } 
    if (filter?.fechaMin) {
      const fecha = filter.fechaMin instanceof DateTime ? filter.fechaMin.startOf('day').toJSDate().toISOString() : filter.fechaMin?.setUTCHours(0,0,0,0);
      comps.push(comparison('fecha', ge(fecha)));
      noFilter = false;
    }
    if (filter?.fechaMax) {
      const fecha = filter.fechaMax instanceof DateTime ? filter.fechaMax.endOf('day').toJSDate().toISOString() : filter.fechaMax?.setUTCHours(23,59,59,99);
      comps.push(comparison('fecha', le(fecha)));
      noFilter = false;
    }
    if (filter?.daGarantia != null) {
      comps.push(comparison('requiereGarantia', eq(filter.daGarantia)));
      noFilter = false;
    }
    if (noFilter) {
      comps.push(comparison('montoRequerido', ge(0)));
    }
  
    let params: HttpParams = null;
    if (comps.length > 0) {
      let q = and(...comps).toString();
      params = new HttpParams().set('rquery', q);
    }
    
    if (filter?.sortByMonto) {
      params = params.append('rsort', 'montoRequerido').append('rdirection', filter.sortByMonto);
    }
    if (filter?.sortByRazonSocial) {
      params = params.append('rsort', 'razonSocial').append('rdirection', filter.sortByRazonSocial);
    }
    if (filter?.sortByScore) {
      params = params.append('rsort', 'score').append('rdirection', filter.sortByScore);
    }
    if (filter?.sortByMotivo) {
      params = params.append('rsort', 'motivoDeFinanciamiento').append('rdirection', filter.sortByMotivo);
    }
    if (filter?.sortByGarantia) {
      params = params.append('rsort', 'requiereGarantia').append('rdirection', filter.sortByGarantia);
    }
    if (filter?.sortByIntencion) {
      params = params.append('rsort', 'intencionCredito').append('rdirection', filter.sortByIntencion);
    }
    if (filter?.sortByStatus) {
      params = params.append('rsort', 'status').append('rdirection', filter.sortByStatus);
    }
    if (filter?.sortByFecha) {
      params = params.append('rsort', 'fecha').append('rdirection', filter.sortByFecha);
    }

    return this.http.get<PageableModel<ICreditLines>>(`${environment.apiBaseUrl}/financiamientos/oferta/portal/get-offers-pyme/${filter.page}/${filter.size}`, {params})
  }

  public getCreatedInvestorOffers(filter: ICreditOffersFilters): Observable<PageableModel<ICreditLines>> {
    const comps = []
    let noFilter = true;

    if (filter.razonSocial) {
      const razonSocialValue = filter.razonSocial.replace(/\s/g, '%');
      comps.push(`razonSocial==*${razonSocialValue}*`);
      noFilter = false;
    }
    if (filter.score) {
      comps.push(comparison('score', eq(filter.score)));
      noFilter = false;
    }
    if (filter.montoMin) {
      comps.push(comparison('montoRequerido', ge(filter.montoMin)));
      noFilter = false;
    }
    if (filter.montoMax) {
      comps.push(comparison('montoRequerido', le(filter.montoMax)));
      noFilter = false;
    }
    if (filter.motivo) {
      const motivoValue = filter.motivo.replace(/\s/g, '%');
      comps.push(`motivoDeFinanciamiento==*${motivoValue}*`);
      noFilter = false;
    }
    if (filter.estado) {
      comps.push(comparison('status', eq(filter.estado)));
      noFilter = false;
    }
    if (filter.intencionMin) {
      comps.push(comparison('intencionCredito', ge(filter.intencionMin)));
      noFilter = false;
    }
    if (filter.intencionMax) {
      comps.push(comparison('intencionCredito', le(filter.intencionMax)));
      noFilter = false;
    } 
    if (filter.fechaMin) {
      const fecha = filter.fechaMin instanceof DateTime ? filter.fechaMin.startOf('day').toJSDate().toISOString() : filter.fechaMin?.setUTCHours(0,0,0,0);
      comps.push(comparison('fecha', ge(fecha)));
      noFilter = false;
    }
    if (filter.fechaMax) {
      const fecha = filter.fechaMax instanceof DateTime ? filter.fechaMax.endOf('day').toJSDate().toISOString() : filter.fechaMax?.setUTCHours(23,59,59,99);
      comps.push(comparison('fecha', le(fecha)));
      noFilter = false;
    }
    if (filter.daGarantia != null) {
      comps.push(comparison('requiereGarantia', eq(filter.daGarantia)));
      noFilter = false;
    }
    if (noFilter) {
      comps.push(comparison('montoRequerido', ge(0)));
    }

    let params: HttpParams = null;
    if (comps.length > 0) {
      let q = and(...comps).toString();
      params = new HttpParams().set('rquery', q);
    }
    
    if (filter.sortByMonto) {
      params = params.append('rsort', 'montoRequerido').append('rdirection', filter.sortByMonto);
    }
    if (filter.sortByRazonSocial) {
      params = params.append('rsort', 'razonSocial').append('rdirection', filter.sortByRazonSocial);
    }
    if (filter.sortByScore) {
      params = params.append('rsort', 'score').append('rdirection', filter.sortByScore);
    }
    if (filter.sortByMotivo) {
      params = params.append('rsort', 'motivoDeFinanciamiento').append('rdirection', filter.sortByMotivo);
    }
    if (filter.sortByGarantia) {
      params = params.append('rsort', 'requiereGarantia').append('rdirection', filter.sortByGarantia);
    }
    if (filter.sortByIntencion) {
      params = params.append('rsort', 'intencionCredito').append('rdirection', filter.sortByIntencion);
    }
    if (filter.sortByStatus) {
      params = params.append('rsort', 'status').append('rdirection', filter.sortByStatus);
    }
    if (filter.sortByFecha) {
      params = params.append('rsort', 'fecha').append('rdirection', filter.sortByFecha);
    }

    return this.http.get<PageableModel<ICreditLines>>(`${environment.apiBaseUrl}/financiamientos/oferta/portal/get-offers-inversor/${filter.page}/${filter.size}`, {params})
  }

  public getOffersByNegotiation(solicitudId: number, investorId: number): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/financiamientos/oferta/portal/get-offers/${solicitudId}/${investorId}`)
  }

  public acceptOffer(body: any): Observable<any> {
    return this.http.put(`${environment.apiBaseUrl}/financiamientos/oferta/portal/accept-offer`, body);
  }
  
  public declineOffer(id: number): Observable<any> {
    return this.http.put(`${environment.apiBaseUrl}/financiamientos/oferta/portal/pyme-cancel-offer/${id}`, {});
  }

  public cancelOffer(id: number): Observable<any> {
    return this.http.put(`${environment.apiBaseUrl}/financiamientos/oferta/portal/investor-cancel-offer/${id}`, {});
  }

  public getOfferSimulation(id: number): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/financiamientos/oferta/portal/get-simulacion-offer/${id}`)
  }
}
